import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import AdminLayout from '../../components/Admin/AdminLayout'
import AdminPricingEditor from '../../components/Admin/AdminPricingEditor'
import { RsIcon, resolveIconNode, IconLabel, ModalClose } from '../../components/icons'
import { useConfirm } from '../../contexts/ConfirmContext'

const ICON_OPTIONS = ['mail', 'barChart', 'clock', 'globe', 'alertTriangle', 'fileText', 'users']

const TABS = [
  { id: 'general', label: 'General', icon: 'palette' },
  { id: 'email', label: 'Email', icon: 'inbox' },
  { id: 'plans', label: 'Plans', icon: 'creditCard' },
  { id: 'features', label: 'Features', icon: 'puzzle' },
  { id: 'integrations', label: 'Integrations', icon: 'link' },
]

const FEATURE_TOGGLES = {
  core: [
    { key: 'findDeal', icon: 'search', cls: 'blue', name: 'Find a Deal', desc: 'Allow users to browse and discover deals' },
    { key: 'dealSubmission', icon: 'pen', cls: 'green', name: 'Deal Submission', desc: 'Allow GPs/LPs/Startups to submit deals' },
    { key: 'ndaWorkflow', icon: 'lock', cls: 'orange', name: 'NDA Workflow', desc: 'Enable NDA signing before viewing deal details' },
    { key: 'dataRoom', icon: 'chart', cls: 'purple', name: 'Data Room', desc: 'Enable virtual data room for document sharing' },
    { key: 'messaging', icon: 'chat', cls: 'blue', name: 'In-Platform Messaging', desc: 'Enable chat between deal participants' },
    { key: 'meetings', icon: 'meeting', cls: 'green', name: 'Meeting Scheduling', desc: 'Enable calendar integration and meeting booking' },
  ],
  beta: [
    { key: 'aiMatching', icon: 'sparkles', cls: 'orange', name: 'AI Deal Matching', desc: 'AI-powered deal recommendations (Beta)' },
    { key: 'advancedAnalytics', icon: 'trendUp', cls: 'purple', name: 'Advanced Analytics', desc: 'Enhanced analytics for Enterprise users' },
    { key: 'apiAccess', icon: 'link', cls: 'blue', name: 'API Access', desc: 'Allow API access for Enterprise users' },
  ],
  security: [
    { key: 'require2fa', icon: 'shield', cls: 'green', name: 'Two-Factor Authentication', desc: 'Require 2FA for all users' },
    { key: 'hardwareKey', icon: 'key', cls: 'orange', name: 'Hardware Key (Admin)', desc: 'Require YubiKey for admin accounts' },
    { key: 'workEmailOnly', icon: 'email', cls: 'blue', name: 'Work Email Only', desc: 'Reject personal email domains during registration' },
  ],
}

const NOTIFICATION_TOGGLES = [
  { key: 'emailNotifications', icon: 'email', cls: 'blue', name: 'Email Notifications', desc: 'Registration rejection, company invites, chat login reminders, and approved email campaigns' },
  { key: 'inAppNotifications', icon: 'mobile', cls: 'green', name: 'In-App Notifications', desc: 'Notification center bell (chat, meetings, digest unread counts)' },
  { key: 'digestEmails', icon: 'timer', cls: 'orange', name: 'Digest Emails', desc: 'Weekly digest summary emails (Friday evening, once per week) to users with unread items' },
  { key: 'dealAlerts', icon: 'bell', cls: 'purple', name: 'Deal Alerts', desc: 'Email matched LPs when a GP posts a new deal' },
]

const MANUAL_INTEGRATIONS = ['wire', 'wechatPay', 'alipay']

const INTEGRATION_META = {
  stripe: { logo: 'creditCard', sub: 'Credit card payments' },
  wire: { logo: 'fund', sub: 'Bank wire instructions' },
  wechatPay: { logo: 'payment', sub: 'Chinese mobile payments' },
  alipay: { logo: 'payment', sub: 'Chinese mobile payments' },
  googleCalendar: { logo: 'calendar', sub: 'Sync meetings with Google Calendar' },
  outlook: { logo: 'email', sub: 'Sync meetings with Outlook' },
  zoom: { logo: 'video', sub: 'Video conferencing integration' },
  linkedin: { logo: 'briefcase', sub: 'Profile verification via LinkedIn' },
}

function Field({ label, hint, children }) {
  return (
    <div className="admin-field-group">
      <label className="admin-field-label">{label}</label>
      {children}
      {hint && <div className="admin-field-hint">{hint}</div>}
    </div>
  )
}

function ToggleRow({ icon, cls, name, desc, checked, onChange }) {
  return (
    <div className="admin-settings-toggle-row">
      <div className="admin-settings-toggle-left">
        <div className={`admin-settings-toggle-icon ${cls}`}>{resolveIconNode(icon, { size: 'md' })}</div>
        <div>
          <div className="admin-settings-toggle-name">{name}</div>
          <div className="admin-settings-toggle-desc">{desc}</div>
        </div>
      </div>
      <label className="admin-toggle">
        <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
        <span className="admin-toggle-slider" />
      </label>
    </div>
  )
}

export default function AdminSettings() {
  const confirm = useConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = searchParams.get('tab') || 'general'
  const setTab = (next) => setSearchParams({ tab: next })

  const [settings, setSettings] = useState(null)
  const [templates, setTemplates] = useState([])
  const [tplForm, setTplForm] = useState(null)
  const [sources, setSources] = useState([])
  const [digestForm, setDigestForm] = useState({ name: '', url: '', keywords: '', language: 'en', is_active: 1 })
  const [editingSourceId, setEditingSourceId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [integrationStatuses, setIntegrationStatuses] = useState({})
  const [connecting, setConnecting] = useState(null)
  const [wireModal, setWireModal] = useState(false)
  const [wireForm, setWireForm] = useState({ bankName: '', accountName: '', accountNumber: '', swift: '' })
  const [smtpPasswordDraft, setSmtpPasswordDraft] = useState('')

  const loadIntegrations = useCallback(async () => {
    try {
      const res = await axios.get('/api/admin/integrations/status')
      setIntegrationStatuses(res.data)
    } catch {
      /* optional on first load */
    }
  }, [])

  const load = useCallback(async () => {
    try {
      const [settingsRes, tplRes, srcRes] = await Promise.all([
        axios.get('/api/admin/cms/platform-settings'),
        axios.get('/api/admin/cms/email-templates'),
        axios.get('/api/digest/sources'),
      ])
      setSettings(settingsRes.data)
      setSmtpPasswordDraft('')
      setTemplates(tplRes.data)
      setSources(srcRes.data)
      loadIntegrations()
    } catch {
      toast.error('Failed to load settings')
    }
  }, [loadIntegrations])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const integration = searchParams.get('integration')
    const success = searchParams.get('success')
    if (integration && success !== null) {
      if (success === '1') {
        toast.success(`${integration} connected successfully`)
      } else {
        toast.error(searchParams.get('message') || `${integration} connection failed`)
      }
      loadIntegrations()
      setSearchParams({ tab: 'integrations' })
    }
  }, [searchParams, setSearchParams, loadIntegrations])

  const patch = (section, data) => {
    setSettings((prev) => ({ ...prev, [section]: { ...prev[section], ...data } }))
  }

  const saveSettings = async (section) => {
    if (!settings) return
    setSaving(true)
    try {
      let payload = section ? { [section]: { ...settings[section] } } : settings
      if (section === 'notifications') {
        const notifications = { ...payload.notifications }
        if (smtpPasswordDraft.trim()) {
          notifications.smtpPassword = smtpPasswordDraft.trim()
        } else {
          delete notifications.smtpPassword
        }
        delete notifications.smtpPasswordConfigured
        payload = { notifications }
      }
      const res = await axios.put('/api/admin/cms/platform-settings', payload)
      setSettings(res.data.settings)
      setSmtpPasswordDraft('')
      toast.success('Settings saved')
    } catch {
      toast.error('Save failed')
    } finally {
      setSaving(false)
    }
  }

  const savePricing = async () => {
    if (!settings) return
    setSaving(true)
    try {
      const res = await axios.put('/api/admin/cms/platform-settings', {
        upgradePricing: settings.upgradePricing,
        membershipPlans: settings.membershipPlans,
      })
      setSettings(res.data.settings)
      toast.success('Pricing saved')
    } catch {
      toast.error('Save failed')
    } finally {
      setSaving(false)
    }
  }

  const saveTemplate = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      if (tplForm.id) {
        await axios.put(`/api/admin/cms/email-templates/${tplForm.id}`, tplForm)
      } else {
        await axios.post('/api/admin/cms/email-templates', tplForm)
      }
      toast.success('Template saved')
      setTplForm(null)
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const saveDigestSource = async (e) => {
    e.preventDefault()
    const payload = {
      name: digestForm.name,
      url: digestForm.url,
      keywords: digestForm.keywords.split(',').map((k) => k.trim()).filter(Boolean),
      language: digestForm.language,
      is_active: digestForm.is_active ? 1 : 0,
    }
    try {
      if (editingSourceId) {
        await axios.put(`/api/digest/sources/${editingSourceId}`, payload)
      } else {
        await axios.post('/api/digest/sources', payload)
      }
      toast.success('Source saved')
      setDigestForm({ name: '', url: '', keywords: '', language: 'en', is_active: 1 })
      setEditingSourceId(null)
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed')
    }
  }

  const fetchDigest = async () => {
    setFetching(true)
    try {
      const res = await axios.post('/api/digest/fetch')
      toast.success(`Fetch complete: ${res.data.added} new items added`)
      load()
    } catch {
      toast.error('Fetch failed')
    } finally {
      setFetching(false)
    }
  }

  const sendTestEmail = async () => {
    setSaving(true)
    try {
      const to = settings?.notifications?.senderEmail || undefined
      const res = await axios.post('/api/admin/cms/send-test-email', to ? { to } : {})
      const suffix = res.data.source ? ` (${res.data.source})` : ''
      toast.success(`${res.data.message || 'Test email sent'}${suffix}`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send test email')
    } finally {
      setSaving(false)
    }
  }

  const deleteTemplate = async (id) => {
    const ok = await confirm({
      title: 'Delete template?',
      message: 'Delete this template? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await axios.delete(`/api/admin/cms/email-templates/${id}`)
      toast.success('Template deleted')
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed')
    }
  }

  const deleteDigestSource = async (id) => {
    const ok = await confirm({
      title: 'Delete digest source?',
      message: 'Delete this digest source? This action cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await axios.delete(`/api/digest/sources/${id}`)
      toast.success('Source deleted')
      load()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed')
    }
  }

  const editDigestSource = (source) => {
    setEditingSourceId(source.id)
    setDigestForm({
      name: source.name || '',
      url: source.url || '',
      keywords: Array.isArray(source.keywords) ? source.keywords.join(', ') : (source.keywords || ''),
      language: source.language || 'en',
      is_active: source.is_active ? 1 : 0,
    })
  }

  const statusBadgeClass = (status) => {
    if (status === 'connected' || status === 'active') return 'connected'
    if (status === 'pending') return 'pending'
    return 'disconnected'
  }

  const statusLabel = (status) => {
    if (status === 'connected' || status === 'active') return 'Connected'
    if (status === 'pending') return 'Pending'
    return 'Not connected'
  }

  const handleIntegrationConnect = async (provider) => {
    if (provider === 'wire') {
      setWireModal(true)
      return
    }
    setConnecting(provider)
    try {
      const res = await axios.get(`/api/admin/integrations/${provider}/connect-url`)
      if (res.data.url) {
        window.location.href = res.data.url
        return
      }
      if (res.data.connected) {
        setIntegrationStatuses(res.data.statuses || {})
        toast.success(res.data.dev ? `${provider} connected (dev mode)` : `${provider} connected`)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Connect failed')
    } finally {
      setConnecting(null)
    }
  }

  const handleIntegrationDisconnect = async (provider) => {
    const ok = await confirm({
      title: 'Disconnect integration?',
      message: `Disconnect ${provider}? You can reconnect later from Platform Settings.`,
      confirmText: 'Disconnect',
      variant: 'warning',
    })
    if (!ok) return
    try {
      const res = await axios.post(`/api/admin/integrations/${provider}/disconnect`)
      setIntegrationStatuses(res.data.statuses || {})
      toast.success('Disconnected')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Disconnect failed')
    }
  }

  const saveWireConfig = async (e) => {
    e.preventDefault()
    try {
      const res = await axios.post('/api/admin/integrations/wire/configure', wireForm)
      setIntegrationStatuses(res.data.statuses || {})
      setWireModal(false)
      toast.success('Wire transfer instructions saved')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed')
    }
  }

  const renderIntegrationRow = (key) => {
    const val = integrationStatuses[key] || {}
    const meta = INTEGRATION_META[key] || { logo: 'link', sub: '' }
    const status = val.status || 'disconnected'
    const isConnected = status === 'connected' || status === 'active'
    const isManual = MANUAL_INTEGRATIONS.includes(key)
    const actionLabel = isConnected ? 'Disconnect' : (isManual ? 'Configure' : 'Connect')
    return (
      <div key={key} className="admin-int-card">
        <div className="admin-int-left">
          <div className="admin-int-logo"><RsIcon name={meta.logo} size="lg" /></div>
          <div>
            <div className="admin-int-name">{val.label || key}</div>
            <div className="admin-int-status">{meta.sub}</div>
            {val.dev && isConnected && <div className="admin-field-hint">Dev mode connection</div>}
          </div>
        </div>
        <div className="admin-int-right">
          <span className={`admin-int-badge ${statusBadgeClass(status)}`}>{statusLabel(status)}</span>
          <button
            type="button"
            className="admin-btn sm secondary"
            disabled={connecting === key}
            onClick={() => (isConnected ? handleIntegrationDisconnect(key) : handleIntegrationConnect(key))}
          >
            {connecting === key ? '…' : actionLabel}
          </button>
        </div>
      </div>
    )
  }

  if (!settings) {
    return (
      <AdminLayout title="Platform Settings" subtitle="Configure platform appearance, notifications, features, and integrations">
        <p style={{ color: 'var(--admin-text-muted)' }}>Loading settings…</p>
      </AdminLayout>
    )
  }

  const g = settings.general || {}
  const n = settings.notifications || {}
  const f = settings.features || {}
  const upgradePricing = settings.upgradePricing || {}
  const membershipPlans = settings.membershipPlans || {}
  const paymentKeys = ['stripe', 'wire', 'wechatPay', 'alipay']
  const calendarKeys = ['googleCalendar', 'outlook', 'zoom', 'linkedin']

  return (
    <AdminLayout title="Platform Settings" subtitle="Configure platform appearance, notifications, features, and integrations">
      <div className="admin-settings-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`admin-settings-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            <IconLabel name={t.icon} size="sm">{t.label}</IconLabel>
          </button>
        ))}
      </div>

      {tab === 'general' && (
        <>
          <div className="admin-settings-grid-2">
            <div className="admin-card">
              <div className="admin-card-header"><span className="admin-card-title">Platform Information</span></div>
              <div className="admin-card-body">
                <Field label="Platform Name">
                  <input className="admin-field-input" value={g.platformName || ''} onChange={(e) => patch('general', { platformName: e.target.value })} />
                </Field>
                <Field label="Tagline">
                  <input className="admin-field-input" value={g.tagline || ''} onChange={(e) => patch('general', { tagline: e.target.value })} />
                </Field>
                <Field label="Support Email">
                  <input className="admin-field-input" type="email" value={g.supportEmail || ''} onChange={(e) => patch('general', { supportEmail: e.target.value })} />
                </Field>
                <Field label="Support Phone">
                  <input className="admin-field-input" value={g.supportPhone || ''} onChange={(e) => patch('general', { supportPhone: e.target.value })} />
                </Field>
                <Field label="Default Language">
                  <select className="admin-field-input" value={g.defaultLanguage || 'en'} onChange={(e) => patch('general', { defaultLanguage: e.target.value })}>
                    <option value="en">English</option>
                    <option value="zh">Chinese</option>
                    <option value="en-zh">English & Chinese</option>
                  </select>
                </Field>
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-card-header"><span className="admin-card-title">Branding</span></div>
              <div className="admin-card-body">
                <Field label="Logo (Light Background)">
                  <input className="admin-field-input" value={g.logoLight || ''} onChange={(e) => patch('general', { logoLight: e.target.value })} />
                  <div className="admin-logo-preview">Preview</div>
                </Field>
                <Field label="Logo (Dark Background)">
                  <input className="admin-field-input" value={g.logoDark || ''} onChange={(e) => patch('general', { logoDark: e.target.value })} />
                  <div className="admin-logo-preview">Preview</div>
                </Field>
                <Field label="Primary Color">
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="color" value={g.primaryColor || '#0f3d8a'} onChange={(e) => patch('general', { primaryColor: e.target.value })} className="admin-color-input" />
                    <input className="admin-field-input" style={{ flex: 1 }} value={g.primaryColor || ''} onChange={(e) => patch('general', { primaryColor: e.target.value })} />
                  </div>
                </Field>
                <Field label="Favicon">
                  <input className="admin-field-input" value={g.favicon || ''} onChange={(e) => patch('general', { favicon: e.target.value })} />
                </Field>
              </div>
            </div>
          </div>
          <div className="admin-card">
            <div className="admin-card-header"><span className="admin-card-title">Legal & Policies</span></div>
            <div className="admin-card-body admin-settings-grid-2">
              <Field label="Terms of Service URL">
                <input className="admin-field-input" value={g.termsUrl || ''} onChange={(e) => patch('general', { termsUrl: e.target.value })} />
              </Field>
              <Field label="Privacy Policy URL">
                <input className="admin-field-input" value={g.privacyUrl || ''} onChange={(e) => patch('general', { privacyUrl: e.target.value })} />
              </Field>
              <Field label="Data Processing Agreement URL">
                <input className="admin-field-input" value={g.dpaUrl || ''} onChange={(e) => patch('general', { dpaUrl: e.target.value })} />
              </Field>
              <Field label="Cookie Policy URL">
                <input className="admin-field-input" value={g.cookiesUrl || ''} onChange={(e) => patch('general', { cookiesUrl: e.target.value })} />
              </Field>
            </div>
          </div>
          <div className="admin-settings-btn-group">
            <button type="button" className="admin-btn primary" disabled={saving} onClick={() => saveSettings('general')}>Save Changes</button>
            <button type="button" className="admin-btn secondary" onClick={() => load()}>Reset</button>
          </div>
        </>
      )}

      {tab === 'email' && (
        <>
          <div className="admin-settings-alert info">
            <RsIcon name="info" size="md" />
            <span>
              Email Settings are used for live system mail (login codes, registration, notifications).
              Leave SMTP host empty to fall back to server environment variables.
            </span>
          </div>
          <div className="admin-settings-alert info">
            <RsIcon name="info" size="md" />
            <span>Email templates support variables like <strong>{'{userName}'}</strong>, <strong>{'{dealName}'}</strong>, <strong>{'{platformName}'}</strong></span>
          </div>
          <div className="admin-card">
            <div className="admin-card-header"><span className="admin-card-title">Email Settings</span></div>
            <div className="admin-card-body admin-settings-grid-2">
              <Field label="Sender Name">
                <input className="admin-field-input" value={n.senderName || ''} onChange={(e) => patch('notifications', { senderName: e.target.value })} />
              </Field>
              <Field label="Sender Email">
                <input className="admin-field-input" type="email" value={n.senderEmail || ''} onChange={(e) => patch('notifications', { senderEmail: e.target.value })} />
              </Field>
              <Field label="SMTP Host" hint="e.g. smtp.exmail.qq.com">
                <input className="admin-field-input" value={n.smtpHost || ''} onChange={(e) => patch('notifications', { smtpHost: e.target.value })} placeholder="smtp.example.com" />
              </Field>
              <Field label="SMTP Port" hint="465 for SSL, 587 for STARTTLS">
                <input className="admin-field-input" type="number" value={n.smtpPort ?? 587} onChange={(e) => patch('notifications', { smtpPort: Number(e.target.value) })} />
              </Field>
              <Field label="SMTP Username">
                <input className="admin-field-input" value={n.smtpUser || ''} onChange={(e) => patch('notifications', { smtpUser: e.target.value })} placeholder="noreply@yourcompany.com" autoComplete="off" />
              </Field>
              <Field
                label="SMTP Password"
                hint={n.smtpPasswordConfigured ? 'Password saved. Leave blank to keep the current password.' : 'Required when SMTP host and username are set.'}
              >
                <input
                  className="admin-field-input"
                  type="password"
                  value={smtpPasswordDraft}
                  onChange={(e) => setSmtpPasswordDraft(e.target.value)}
                  placeholder={n.smtpPasswordConfigured ? '••••••••' : 'Enter SMTP password'}
                  autoComplete="new-password"
                />
              </Field>
              <Field label="Use SSL/TLS (port 465)">
                <label className="admin-toggle" style={{ marginTop: 4 }}>
                  <input
                    type="checkbox"
                    checked={!!n.smtpSecure}
                    onChange={(e) => patch('notifications', { smtpSecure: e.target.checked })}
                  />
                  <span className="admin-toggle-slider" />
                </label>
              </Field>
            </div>
          </div>
          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Email Templates</span>
              <button
                type="button"
                className="admin-btn sm"
                onClick={() => setTplForm({ name: '', slug: '', subject: '', body_html: '<p></p>', description: '', icon_key: 'mail', sort_order: templates.length + 1, is_active: 1 })}
              >
                New Template
              </button>
            </div>
            <div className="admin-card-body flush">
              <div className="admin-table-card embedded">
                <table>
                  <thead><tr><th>Name</th><th>Subject</th><th>Actions</th></tr></thead>
                  <tbody>
                    {templates.map((t) => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td>{t.subject}</td>
                        <td>
                          <button type="button" className="admin-btn sm secondary" onClick={() => setTplForm({ ...t, is_active: t.is_active ? 1 : 0 })}>Edit</button>
                          <button type="button" className="admin-btn sm danger" onClick={() => deleteTemplate(t.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                    {!templates.length && (
                      <tr><td colSpan={3} style={{ textAlign: 'center', color: '#909399', padding: 20 }}>No templates yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="admin-card">
            <div className="admin-card-header"><span className="admin-card-title">Notification Rules</span></div>
            <div className="admin-card-body">
              {NOTIFICATION_TOGGLES.map((t) => (
                <ToggleRow key={t.key} {...t} checked={n[t.key]} onChange={(v) => patch('notifications', { [t.key]: v })} />
              ))}
            </div>
          </div>
          <div className="admin-settings-btn-group">
            <button type="button" className="admin-btn primary" disabled={saving} onClick={() => saveSettings('notifications')}>Save Changes</button>
            <button type="button" className="admin-btn secondary" disabled={saving} onClick={sendTestEmail}>Send Test Email</button>
          </div>
        </>
      )}

      {tab === 'plans' && (
        <>
          <div className="admin-settings-alert info">
            <RsIcon name="info" size="md" />
            <span>
              All amounts shown on the Payment / Upgrade page are managed here.
              GP, LP, and Startup use the upgrade builder; other roles use membership tiers below.
            </span>
          </div>
          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Payment &amp; Upgrade Pricing</span>
            </div>
            <div className="admin-card-body">
              <AdminPricingEditor
                upgradePricing={upgradePricing}
                membershipPlans={membershipPlans}
                onPatchUpgrade={(data) => patch('upgradePricing', data)}
                onPatchMembership={(data) => patch('membershipPlans', data)}
              />
            </div>
          </div>
          <div className="admin-settings-btn-group">
            <button type="button" className="admin-btn primary" disabled={saving} onClick={savePricing}>Save Pricing</button>
            <button type="button" className="admin-btn secondary" onClick={() => load()}>Reset</button>
          </div>
        </>
      )}

      {tab === 'features' && (
        <>
          <div className="admin-settings-alert warning">
            <RsIcon name="warning" size="md" />
            <span>Feature toggles affect all users immediately. Disable features with caution.</span>
          </div>
          {[
            { title: 'Core Features', items: FEATURE_TOGGLES.core },
            { title: 'Beta Features', items: FEATURE_TOGGLES.beta },
            { title: 'Security Features', items: FEATURE_TOGGLES.security },
          ].map((section) => (
            <div key={section.title} className="admin-card">
              <div className="admin-card-header"><span className="admin-card-title">{section.title}</span></div>
              <div className="admin-card-body">
                {section.items.map((t) => (
                  <ToggleRow key={t.key} {...t} checked={f[t.key]} onChange={(v) => patch('features', { [t.key]: v })} />
                ))}
              </div>
            </div>
          ))}
          <div className="admin-settings-btn-group">
            <button type="button" className="admin-btn primary" disabled={saving} onClick={() => saveSettings('features')}>Save Changes</button>
          </div>
        </>
      )}

      {tab === 'integrations' && (
        <>
          <div className="admin-settings-alert info">
            <RsIcon name="info" size="md" />
            <span>OAuth uses backend/.env credentials. OAUTH_DEV_MODE enables dev connections without real OAuth apps.</span>
          </div>
          <div className="admin-card">
            <div className="admin-card-header"><span className="admin-card-title">Payment Gateways</span></div>
            <div className="admin-card-body admin-int-list">{paymentKeys.map(renderIntegrationRow)}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card-header"><span className="admin-card-title">Calendar & Scheduling</span></div>
            <div className="admin-card-body admin-int-list">{calendarKeys.map(renderIntegrationRow)}</div>
          </div>
          <div className="admin-card">
            <div className="admin-card-header">
              <span className="admin-card-title">Industry Digest Sources</span>
              <button type="button" className="admin-btn sm" onClick={fetchDigest} disabled={fetching}>{fetching ? 'Fetching…' : 'Fetch Now'}</button>
            </div>
            <div className="admin-card-body">
              <form onSubmit={saveDigestSource} className="admin-settings-grid-2" style={{ marginBottom: 16 }}>
                <input className="admin-field-input" placeholder="Source name" value={digestForm.name} onChange={(e) => setDigestForm((f) => ({ ...f, name: e.target.value }))} required />
                <input className="admin-field-input" placeholder="RSS / URL" value={digestForm.url} onChange={(e) => setDigestForm((f) => ({ ...f, url: e.target.value }))} required />
                <input className="admin-field-input" placeholder="Keywords (comma separated)" value={digestForm.keywords} onChange={(e) => setDigestForm((f) => ({ ...f, keywords: e.target.value }))} />
                <button type="submit" className="admin-btn primary">{editingSourceId ? 'Update Source' : 'Add Source'}</button>
              </form>
              <div className="admin-table-card embedded">
                <table>
                  <thead><tr><th>Name</th><th>URL</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {sources.map((s) => (
                      <tr key={s.id}>
                        <td>{s.name}</td>
                        <td style={{ fontSize: 11 }}>{s.url}</td>
                        <td>{s.is_active ? 'Active' : 'Inactive'}</td>
                        <td>
                          <button type="button" className="admin-btn sm secondary" onClick={() => editDigestSource(s)}>Edit</button>
                          <button type="button" className="admin-btn sm danger" onClick={() => deleteDigestSource(s.id)}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {wireModal && (
        <div className="admin-modal-overlay show" onClick={() => setWireModal(false)}>
          <form onSubmit={saveWireConfig} className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Wire Transfer Instructions</h3>
              <ModalClose onClick={() => setWireModal(false)} />
            </div>
            <div className="admin-modal-body">
              <Field label="Bank Name"><input className="admin-field-input" value={wireForm.bankName} onChange={(e) => setWireForm((f) => ({ ...f, bankName: e.target.value }))} required /></Field>
              <Field label="Account Name"><input className="admin-field-input" value={wireForm.accountName} onChange={(e) => setWireForm((f) => ({ ...f, accountName: e.target.value }))} required /></Field>
              <Field label="Account Number"><input className="admin-field-input" value={wireForm.accountNumber} onChange={(e) => setWireForm((f) => ({ ...f, accountNumber: e.target.value }))} required /></Field>
              <Field label="SWIFT / Routing"><input className="admin-field-input" value={wireForm.swift} onChange={(e) => setWireForm((f) => ({ ...f, swift: e.target.value }))} /></Field>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={() => setWireModal(false)}>Cancel</button>
              <button type="submit" className="admin-btn primary">Save</button>
            </div>
          </form>
        </div>
      )}

      {tplForm && (
        <div className="admin-modal-overlay show" onClick={() => setTplForm(null)}>
          <form onSubmit={saveTemplate} className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{tplForm.id ? 'Edit Template' : 'New Template'}</h3>
              <ModalClose onClick={() => setTplForm(null)} />
            </div>
            <div className="admin-modal-body">
              <Field label="Name"><input className="admin-field-input" value={tplForm.name} onChange={(e) => setTplForm((f) => ({ ...f, name: e.target.value }))} required /></Field>
              <Field label="Subject"><input className="admin-field-input" value={tplForm.subject} onChange={(e) => setTplForm((f) => ({ ...f, subject: e.target.value }))} required /></Field>
              <Field label="Icon">
                <select className="admin-field-input" value={tplForm.icon_key || 'mail'} onChange={(e) => setTplForm((f) => ({ ...f, icon_key: e.target.value }))}>
                  {ICON_OPTIONS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
              <Field label="Body (HTML)">
                <textarea className="admin-field-input" style={{ minHeight: 120 }} value={tplForm.body_html} onChange={(e) => setTplForm((f) => ({ ...f, body_html: e.target.value }))} required />
              </Field>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn secondary" onClick={() => setTplForm(null)}>Cancel</button>
              <button type="submit" className="admin-btn primary" disabled={saving}>Save</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  )
}
